import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { COLORS } from "../assets/theme";
import {
  getCachedSuperadmin,
  getSuperadminMe,
  superadminLogout,
  SuperadminAdmin,
} from "../services/superadminService";

const NAV_ITEMS = [
  { label: "Organizations", to: "/superadmin/organizations" },
  { label: "Features", to: "/superadmin/features" },
];

const SuperadminLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [admin, setAdmin] = useState<SuperadminAdmin | null>(
    getCachedSuperadmin()
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    getSuperadminMe()
      .then(setAdmin)
      .catch(() => {
        // A 401 is already handled by the panel's interceptor.
      });
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: COLORS.MainBackground }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: COLORS.BlackText,
          borderBottom: `1px solid ${COLORS.BorderColor}`,
        }}
      >
        <Toolbar sx={{ gap: { xs: 1, sm: 3 }, flexWrap: "wrap" }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
            onClick={() => navigate("/superadmin/organizations")}
          >
            <ShieldOutlinedIcon sx={{ color: COLORS.WhiteText }} />
            <Typography
              sx={{ color: COLORS.WhiteText, fontWeight: 700, fontSize: "1.05rem" }}
            >
              Platform
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 0.5 }}>
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                style={{ textDecoration: "none" }}
              >
                {({ isActive }) => (
                  <Button
                    disableRipple
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      px: 2,
                      color: isActive
                        ? COLORS.WhiteText
                        : "rgba(255,255,255,0.62)",
                      borderBottom: isActive
                        ? `2px solid ${COLORS.MainColor}`
                        : "2px solid transparent",
                      borderRadius: 0,
                      "&:hover": { background: "rgba(255,255,255,0.06)" },
                    }}
                  >
                    {item.label}
                  </Button>
                )}
              </NavLink>
            ))}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            onClick={(e) => setAnchorEl(e.currentTarget)}
            startIcon={<AccountCircleIcon />}
            sx={{ color: COLORS.WhiteText, textTransform: "none" }}
          >
            {!isMobile && (admin?.fullName || admin?.phone || "Superadmin")}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem disabled sx={{ opacity: "1 !important" }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                  {admin?.fullName || "Superadmin"}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.8rem", color: COLORS.placeHolderColor }}
                >
                  {admin?.phone || ""}
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                superadminLogout();
              }}
            >
              Log out everywhere
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: "auto" }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default SuperadminLayout;
